import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { LanguageSelector } from '../components/LanguageSelector';
import BrandMark from '../components/BrandMark';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Eye, EyeOff, Shield, CheckCircle, Rocket, Sparkles, Sun, Moon, AlertCircle, Mail } from 'lucide-react';

const languageOptions = [
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺' }
];

export default function RegisterPage() {
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    password: '',
    business_name: '',
    default_currency: 'EUR',
    language: 'it'
  });
  const [loading, setLoading] = useState(false);
  const [registrationAvailability, setRegistrationAvailability] = useState({
    status: 'loading',
    enabled: null,
    supportEmail: ''
  });
  const [suspensionDialogOpen, setSuspensionDialogOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const { register, api } = useAuth();
  const { t, changeLanguage } = useLanguage();
  const { isDark, toggleTheme } = useTheme();
  const dialogRef = useRef(null);
  const closeDialogRef = useRef(null);
  const suspendedHeadingRef = useRef(null);
  const wasDialogOpenRef = useRef(false);

  const fetchRegistrationAvailability = async () => {
    setRegistrationAvailability({ status: 'loading', enabled: null, supportEmail: '' });
    try {
      const response = await api.get('/public/registration-status');
      const { registration_enabled: enabled, support_email: supportEmail } = response.data || {};
      if (typeof enabled !== 'boolean' || typeof supportEmail !== 'string') {
        throw new Error('Invalid registration status response');
      }
      setRegistrationAvailability({ status: 'ready', enabled, supportEmail });
      setSuspensionDialogOpen(!enabled);
    } catch (error) {
      setRegistrationAvailability({ status: 'error', enabled: null, supportEmail: '' });
      setSuspensionDialogOpen(false);
    }
  };

  useEffect(() => {
    let active = true;
    api.get('/public/registration-status')
      .then((response) => {
        if (!active) return;
        const { registration_enabled: enabled, support_email: supportEmail } = response.data || {};
        if (typeof enabled !== 'boolean' || typeof supportEmail !== 'string') {
          throw new Error('Invalid registration status response');
        }
        setRegistrationAvailability({ status: 'ready', enabled, supportEmail });
        setSuspensionDialogOpen(!enabled);
      })
      .catch(() => {
        if (!active) return;
        setRegistrationAvailability({ status: 'error', enabled: null, supportEmail: '' });
        setSuspensionDialogOpen(false);
      });
    return () => { active = false; };
  }, [api]);

  useEffect(() => {
    if (wasDialogOpenRef.current && !suspensionDialogOpen) {
      window.requestAnimationFrame?.(() => suspendedHeadingRef.current?.focus());
    }
    wasDialogOpenRef.current = suspensionDialogOpen;
  }, [suspensionDialogOpen]);

  useEffect(() => {
    if (!suspensionDialogOpen || registrationAvailability.status !== 'ready' || registrationAvailability.enabled) return undefined;

    closeDialogRef.current?.focus();
    const handleDialogKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setSuspensionDialogOpen(false);
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusable = dialogRef.current.querySelectorAll('a[href], button:not([disabled])');
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleDialogKeyDown);
    return () => document.removeEventListener('keydown', handleDialogKeyDown);
  }, [suspensionDialogOpen, registrationAvailability]);

  const validatePassword = (password) => {
    if (password.length < 8) return 'La password deve essere di almeno 8 caratteri';
    if (!/[A-Z]/.test(password)) return 'La password deve contenere almeno una lettera maiuscola';
    if (!/[a-z]/.test(password)) return 'La password deve contenere almeno una lettera minuscola';
    if (!/\d/.test(password)) return 'La password deve contenere almeno un numero';
    return '';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (name === 'password') {
      setPasswordError(validatePassword(value));
    }
  };

  const handleLanguageChange = (value) => {
    setFormData({ ...formData, language: value });
    changeLanguage(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (registrationAvailability.status !== 'ready' || !registrationAvailability.enabled) return;
    
    const pwError = validatePassword(formData.password);
    if (pwError) {
      toast.error(pwError);
      return;
    }
    
    setLoading(true);
    try {
      const registrationData = { ...formData };
      if (!registrationData.username.trim()) {
        delete registrationData.username;
      }
      await register(registrationData);
      toast.success(t('auth.registerSuccess'));
      window.location.href = '/dashboard';
    } catch (error) {
      const detail = error.response?.data?.detail;
      const errorCode = typeof detail === 'object' ? detail?.code : detail;
      if (errorCode === 'registration_disabled') {
        setRegistrationAvailability((current) => ({ ...current, status: 'ready', enabled: false }));
        setSuspensionDialogOpen(true);
      } else {
        toast.error(typeof detail === 'string' ? detail : t('auth.registrationError'));
      }
      setLoading(false);
    }
  };

  const isSuspended = registrationAvailability.status === 'ready' && registrationAvailability.enabled === false;

  const benefits = [
    { icon: Rocket, text: 'Attivazione istantanea senza verifiche' },
    { icon: Sparkles, text: 'Dashboard completa inclusa' },
    { icon: CheckCircle, text: 'Multi-gateway: PayPal, Stripe e altri' },
    { icon: Shield, text: 'Transazioni sicure e protette' },
  ];

  return (
    <div className={`hola-auth-page min-h-screen flex ${isDark ? 'is-dark' : ''}`}>
      {/* Left side - Hero/Branding */}
      <div className="hola-auth-brand hidden lg:flex lg:w-1/2 relative overflow-hidden">
        {/* Gradient Background */}
        <div className={`absolute inset-0 ${
          isDark 
            ? 'bg-gradient-to-br from-[#12002F] via-[#2A005F] to-[#0B0F2A]' 
            : 'bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-500'
        }`} />
        
        {/* Decorative Elements */}
        <div className="absolute inset-0 overflow-hidden">
          {isDark ? (
            <>
              <div className="absolute -top-24 -left-24 w-96 h-96 bg-purple-500/20 rounded-full blur-[150px]" />
              <div className="absolute top-1/2 -right-24 w-80 h-80 bg-[#9D4DFF]/30 rounded-full blur-[150px]" />
              <div className="absolute -bottom-24 left-1/3 w-72 h-72 bg-[#6A00FF]/30 rounded-full blur-[150px]" />
            </>
          ) : (
            <>
              <div className="absolute -top-24 -left-24 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
              <div className="absolute top-1/2 -right-24 w-80 h-80 bg-cyan-400/20 rounded-full blur-3xl" />
              <div className="absolute -bottom-24 left-1/3 w-72 h-72 bg-emerald-400/20 rounded-full blur-3xl" />
            </>
          )}
          
          {/* Grid Pattern */}
          <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        
        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center p-12 xl:p-16">
          {/* Logo */}
          <div className="flex flex-col items-start gap-3 mb-12">
            <BrandMark variant="wordmark" theme="dark" size="lg" />
            <p className="text-white/70 text-sm">Gateway di pagamento</p>
          </div>
          
          {/* Main Text */}
          <div className="mb-12">
            <h2 className="text-4xl xl:text-5xl font-bold text-white mb-6 leading-tight">
              Inizia subito<br />
              <span className="hola-auth-accent">
                gratuitamente
              </span>
            </h2>
            <p className="text-white/80 text-lg max-w-md">
              Crea il tuo account e inizia ad accettare pagamenti in pochi minuti.
            </p>
          </div>
          
          {/* Benefits List */}
          <div className="space-y-4">
            {benefits.map((benefit, index) => (
              <div 
                key={index}
                className={`flex items-center gap-4 backdrop-blur-sm rounded-xl px-5 py-4 border ${
                  isDark 
                    ? 'bg-white/5 border-purple-500/30' 
                    : 'bg-white/10 border-white/20'
                }`}
              >
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  isDark ? 'bg-purple-500/30' : 'bg-white/20'
                }`}>
                  <benefit.icon className={`w-5 h-5 ${isDark ? 'text-[#9D4DFF]' : 'text-white'}`} />
                </div>
                <span className="text-white font-medium">{benefit.text}</span>
              </div>
            ))}
          </div>
          
          {/* Trust Badge */}
          <div className={`mt-12 pt-8 border-t ${isDark ? 'border-purple-500/30' : 'border-white/20'}`}>
            <p className="text-white/60 text-sm flex items-center gap-2">
              <Shield className="w-4 h-4" />
              I tuoi dati sono al sicuro con crittografia SSL
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Register Form */}
      <div className={`hola-auth-form flex-1 lg:w-1/2 flex items-center justify-center p-6 sm:p-8 lg:p-12 overflow-y-auto ${
        isDark ? 'bg-[#0B0F2A]' : 'bg-gradient-to-br from-slate-50 to-slate-100'
      }`}>
        <div className="w-full max-w-md py-6">
          {/* Mobile Header */}
          <div className="flex items-center justify-between mb-6">
            <Link to="/" className={`inline-flex items-center gap-2 transition-colors font-medium ${
              isDark ? 'text-gray-400 hover:text-[#9D4DFF]' : 'text-slate-600 hover:text-teal-600'
            }`}>
              <ArrowLeft className="w-4 h-4" />
              {t('auth.backToHome')}
            </Link>
            <div className="flex items-center gap-2">
              <button 
                onClick={toggleTheme}
                className={`w-8 h-8 flex items-center justify-center rounded-full transition-all hover:scale-110 ${
                  isDark 
                    ? 'bg-purple-500/20 text-yellow-400 hover:bg-purple-500/30' 
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid="theme-toggle"
              >
                {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </button>
              <LanguageSelector />
            </div>
          </div>
          
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-3 mb-6">
            <BrandMark variant="wordmark" theme={isDark ? 'dark' : 'light'} size="sm" />
          </div>

          {/* Form Card */}
          <div className={`hola-auth-card rounded-2xl shadow-xl p-6 sm:p-8 border ${
            isDark 
              ? 'bg-[#0F0F2F] border-purple-500/30 shadow-purple-500/10' 
              : 'bg-white border-slate-100 shadow-slate-200/50'
          }`}>
            <div className="text-center mb-6">
              <div className="flex items-center justify-center gap-2 mb-2">
                <h1 className={`min-w-0 text-2xl sm:text-3xl font-bold leading-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  {t('auth.createAccount')}
                </h1>
                <img
                  src="/holapay-cat.png"
                  alt=""
                  aria-hidden="true"
                  width="36"
                  height="36"
                  className="h-8 w-8 shrink-0 object-contain sm:h-9 sm:w-9"
                />
              </div>
              <p className={isDark ? 'text-gray-400' : 'text-slate-500'}>
                {t('auth.registerSubtitle')}
              </p>
            </div>

            {registrationAvailability.status === 'loading' && (
              <div className="flex min-h-48 flex-col items-center justify-center gap-3 text-center" role="status" aria-live="polite" data-testid="registration-status-loading">
                <Loader2 className="h-7 w-7 animate-spin text-teal-600" />
                <p className={isDark ? 'text-gray-300' : 'text-slate-600'}>{t('auth.registrationStatusLoading')}</p>
              </div>
            )}

            {registrationAvailability.status === 'error' && (
              <div className="space-y-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-amber-900" role="alert" data-testid="registration-status-error">
                <div className="flex items-start gap-3">
                  <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
                  <p className="text-sm">{t('auth.registrationStatusError')}</p>
                </div>
                <Button type="button" variant="outline" onClick={fetchRegistrationAvailability} className="w-full" data-testid="registration-status-retry">
                  {t('auth.registrationStatusRetry')}
                </Button>
              </div>
            )}

            {isSuspended && (
              <div className="space-y-4 rounded-xl border border-amber-200 bg-amber-50 p-5 text-amber-950" data-testid="registration-suspended-state">
                <h2 ref={suspendedHeadingRef} tabIndex="-1" className="text-lg font-semibold outline-none">
                  {t('auth.registrationSuspendedTitle')}
                </h2>
                <p className="text-sm leading-relaxed">{t('auth.registrationSuspendedMessage')}</p>
                <a href={`mailto:${registrationAvailability.supportEmail}`} className="inline-flex items-center gap-2 font-semibold text-teal-800 underline underline-offset-4">
                  <Mail className="h-4 w-4" />
                  {t('auth.contactSupport')}
                </a>
                <Button type="button" variant="outline" onClick={() => setSuspensionDialogOpen(true)} className="w-full">
                  {t('auth.registrationSuspendedDetails')}
                </Button>
              </div>
            )}

            {registrationAvailability.status === 'ready' && registrationAvailability.enabled && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="business_name" className={`font-medium ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>{t('auth.businessName')}</Label>
                <Input
                  id="business_name"
                  name="business_name"
                  placeholder="La Mia Azienda"
                  value={formData.business_name}
                  onChange={handleChange}
                  data-testid="register-business"
                  className={`h-11 rounded-xl ${
                    isDark 
                      ? 'bg-white/5 border-purple-500/30 text-white placeholder:text-gray-500 focus:border-[#9D4DFF] focus:ring-[#9D4DFF]/20' 
                      : 'bg-slate-50 border-slate-200 focus:border-teal-500 focus:ring-teal-500/20'
                  }`}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="username" className={`whitespace-nowrap text-xs font-medium ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>{t('auth.usernameOptional')}</Label>
                  <Input
                    id="username"
                    name="username"
                    placeholder={t('common.name')}
                    value={formData.username}
                    onChange={handleChange}
                    data-testid="register-username"
                    className={`h-11 rounded-xl ${
                      isDark 
                        ? 'bg-white/5 border-purple-500/30 text-white placeholder:text-gray-500 focus:border-[#9D4DFF] focus:ring-[#9D4DFF]/20' 
                        : 'bg-slate-50 border-slate-200 focus:border-teal-500 focus:ring-teal-500/20'
                    }`}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="currency" className={`whitespace-nowrap text-xs font-medium ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>{t('common.currency')}</Label>
                  <Select 
                    value={formData.default_currency} 
                    onValueChange={(value) => setFormData({ ...formData, default_currency: value })}
                  >
                    <SelectTrigger data-testid="register-currency" className={`h-11 rounded-xl ${
                      isDark 
                        ? 'bg-white/5 border-purple-500/30 text-white' 
                        : 'bg-slate-50 border-slate-200'
                    }`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className={isDark ? 'bg-[#0F0F2F] border-purple-500/30' : ''}>
                      <SelectItem value="EUR">EUR (€)</SelectItem>
                      <SelectItem value="USD">USD ($)</SelectItem>
                      <SelectItem value="GBP">GBP (£)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="language" className={`font-medium ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>{t('common.language')}</Label>
                <Select 
                  value={formData.language} 
                  onValueChange={handleLanguageChange}
                >
                  <SelectTrigger data-testid="register-language" className={`h-11 rounded-xl ${
                    isDark 
                      ? 'bg-white/5 border-purple-500/30 text-white' 
                      : 'bg-slate-50 border-slate-200'
                  }`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className={isDark ? 'bg-[#0F0F2F] border-purple-500/30' : ''}>
                    {languageOptions.map((lang) => (
                      <SelectItem key={lang.code} value={lang.code}>
                        <span className="flex items-center gap-2">
                          <span>{lang.flag}</span>
                          <span>{lang.name}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email" className={`font-medium ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>{t('common.email')}</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  placeholder="nome@azienda.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  data-testid="register-email"
                  className={`h-11 rounded-xl ${
                    isDark 
                      ? 'bg-white/5 border-purple-500/30 text-white placeholder:text-gray-500 focus:border-[#9D4DFF] focus:ring-[#9D4DFF]/20' 
                      : 'bg-slate-50 border-slate-200 focus:border-teal-500 focus:ring-teal-500/20'
                  }`}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password" className={`font-medium ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>{t('common.password')}</Label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    minLength={8}
                    data-testid="register-password"
                    className={`h-11 rounded-xl pr-12 ${
                      isDark 
                        ? 'bg-white/5 border-purple-500/30 text-white placeholder:text-gray-500 focus:border-[#9D4DFF] focus:ring-[#9D4DFF]/20' 
                        : 'bg-slate-50 border-slate-200 focus:border-teal-500 focus:ring-teal-500/20'
                    } ${passwordError ? 'border-red-400' : formData.password.length >= 8 ? 'border-green-400' : ''}`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className={`absolute right-4 top-1/2 -translate-y-1/2 transition-colors ${
                      isDark ? 'text-gray-500 hover:text-[#9D4DFF]' : 'text-slate-400 hover:text-teal-600'
                    }`}
                    data-testid="toggle-password"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {passwordError && formData.password && (
                  <p className="text-xs text-red-500">{passwordError}</p>
                )}
                {!passwordError && formData.password.length >= 8 && (
                  <p className="text-xs text-green-600 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" /> Password valida
                  </p>
                )}
                <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
                  Min. 8 caratteri, 1 maiuscola, 1 minuscola, 1 numero
                </p>
              </div>
              
              <Button 
                type="submit" 
                className={`hola-auth-submit w-full h-12 font-semibold rounded-xl shadow-lg transition-all duration-300 mt-2 ${
                  isDark 
                    ? 'bg-gradient-to-r from-[#6A00FF] to-[#9D4DFF] hover:from-[#7B1FFF] hover:to-[#AE6EFF] shadow-purple-500/30' 
                    : 'bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 shadow-teal-500/30'
                }`} 
                disabled={loading}
                data-testid="register-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    {t('auth.registering')}
                  </>
                ) : (
                  t('auth.registerButton')
                )}
              </Button>
            </form>
            )}

            <div className={`mt-6 pt-6 border-t text-center ${isDark ? 'border-purple-500/20' : 'border-slate-100'}`}>
              <p className={isDark ? 'text-gray-400' : 'text-slate-600'}>
                {t('auth.hasAccount')}{' '}
                <Link to="/login" className="hola-auth-link font-semibold">
                  {t('common.login')}
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>

      {isSuspended && suspensionDialogOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/60 p-4" data-testid="registration-suspended-backdrop">
          <div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="registration-suspended-title"
            aria-describedby="registration-suspended-description"
            className={`w-full max-w-md rounded-2xl p-6 shadow-2xl ${isDark ? 'border border-purple-500/30 bg-[#0F0F2F] text-white' : 'bg-white text-slate-900'}`}
            data-testid="registration-suspended-dialog"
          >
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 text-amber-700" aria-hidden="true">
              <AlertCircle className="h-6 w-6" />
            </div>
            <h2 id="registration-suspended-title" className="text-xl font-bold">
              {t('auth.registrationSuspendedTitle')}
            </h2>
            <p id="registration-suspended-description" className={`mt-3 text-sm leading-relaxed ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
              {t('auth.registrationSuspendedMessage')}
            </p>
            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <Button ref={closeDialogRef} type="button" variant="outline" onClick={() => setSuspensionDialogOpen(false)}>
                {t('auth.close')}
              </Button>
              <a
                href={`mailto:${registrationAvailability.supportEmail}`}
                className="inline-flex min-h-10 items-center justify-center gap-2 rounded-md bg-teal-600 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600"
                data-testid="registration-contact-support"
              >
                <Mail className="h-4 w-4" />
                {t('auth.contactSupport')}
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
